import { EditorWithAi } from '@/components/formBuilder/customFields';
import './jobInfo.css';

export const jobsEmploymentsOptions = [
  { label: 'Contract', value: 'contract' },
  { label: 'Full Time', value: 'full_time' },
  { label: 'Internship', value: 'internship' },
  { label: 'Part Time', value: 'part_time' },
  { label: 'Seasonal', value: 'seasonal' },
  { label: 'Temporary', value: 'temporary' },
];

export const jobEducationOptions = [
  { label: 'Associate Degree', value: 'associate' },
  { label: "Bachelor's Degree", value: 'bachelor' },
  { label: 'Certification', value: 'certification' },
  { label: 'Doctorate', value: 'doctorate' },
  { label: 'High School or equivalent', value: 'high_school' },
  { label: "Master's Degree", value: 'master' },
];

export const jobExperienceOptions = [
  { label: 'No experience (Intern)', value: 'zero' },
  { label: '0-2 years', value: 'zero_two' },
  { label: '2-5 years', value: 'two_five' },
  { label: '5-10 years', value: 'five_ten' },
  { label: '10-15 years', value: 'ten_fifteen' },
  { label: 'Over 15 years', value: 'over_fifteen' },
];

export const averageTimeInCompanyOptions = [
  { label: '1-2 years', value: 'one_two' },
  { label: '2-3 years', value: 'two_three' },
  { label: '3-4 years', value: 'three_four' },
  { label: '4-5 years', value: 'four_five' },
  { label: 'Over 5 years', value: 'over_five' },
];

const aiScoringPriorityItems = [
  'skills',
  'experience',
  'average_time_in_company',
  'education',
];

const autoRejectMap = { true: 'yes', false: 'no' };
const autoRejectParse = { yes: true, no: false };
const aiScorePriorityMap = (attribute_priority) => {
  return aiScoringPriorityItems
    .map((item) => ({
      id: item,
      enabled: attribute_priority?.includes(item),
    }))
    .sort((a, b) => {
      const indexA = attribute_priority.indexOf(a.id);
      const indexB = attribute_priority.indexOf(b.id);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
};

const processOffices = (currUserSelected, dataFromApi) => {
  if (!Array.isArray(currUserSelected) && !currUserSelected?.length) {
    return [];
  }

  const defaultOfficesId = dataFromApi?.map((i) => i?.id);

  let commonToBoth = currUserSelected.filter((value) =>
    defaultOfficesId.includes(value)
  );

  let itemsToKeep = currUserSelected.filter(
    (value) => !defaultOfficesId.includes(value)
  );

  let itemsToRemove = defaultOfficesId.filter(
    (value) => !currUserSelected.includes(value)
  );

  // Mapping common values and unique values from A
  let toAppend = [...commonToBoth, ...itemsToKeep].map((item) => ({
    ats_office_id: item,
  }));

  // Mapping unique values from B with _destroy property
  let toDestroy = itemsToRemove.map((item) => ({
    ats_office_id: item,
    _destroy: '1',
  }));

  return [...toAppend, ...toDestroy];
};

function generateCustomPayload(obj) {
  const customObject = {};
  const payload = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('custom_')) {
      const newKey = key.replace('custom_', '');
      customObject[newKey] = value;
    } else {
      payload[key] = value;
    }
  }

  return {
    customObject,
    payload,
  };
}

export const createJobInfoPayload = ({
  values = {},
  jobData = {},
  isSettings = false,
  rescreenInfo = {},
  compensationDetails = {},
  isAiScoringV2,
}) => {
  const { basic_info = {}, compensation = {} } = jobData;
  const initialOffices = basic_info?.office;

  const {
    education,
    experience,
    skills,
    text,
    ats_office_id,
    requisitions_codes,
    currency_country_id,
    min_amount,
    max_amount,
    interval,
    auto_reject_threshold,
    average_time_in_company,
    auto_reject,
    aiScoreToggle,
    aiScorePriority,
    useLegacy,
    ...otherValues
  } = values;

  const { customObject = {}, payload } = generateCustomPayload(otherValues);

  if (Object.keys(customObject)?.length > 0) {
    payload.additional_info = customObject;
  }

  if (requisitions_codes) {
    payload.requisitions_codes = Array.isArray(requisitions_codes)
      ? requisitions_codes
      : [requisitions_codes];
  } else {
    payload.requisitions_codes = [];
  }

  if (useLegacy) {
    payload.use_legacy_flow = useLegacy;
  }

  const { base_salary } = compensation || {};

  if (compensationDetails?.enabled) {
    payload.ats_job_salaries_attributes = [
      {
        ...(base_salary?.id && { id: base_salary.id }),
        kind: 'base',
        currency_country_id,
        min_amount: parseInt(min_amount),
        max_amount: parseInt(max_amount),
        interval,
      },
    ];
  }

  if (education || experience || skills) {
    const aiScoringAttributes = { education, skills };
    if (experience) {
      const exp = jobExperienceOptions?.find((it) => it?.value === experience);
      aiScoringAttributes.experience = exp;
    } else {
      aiScoringAttributes.experience = '';
    }
    if (isSettings) {
      aiScoringAttributes.rescreen = rescreenInfo?.reScreenValue;
    }
    aiScoringAttributes.enabled = aiScoreToggle;
    if (average_time_in_company) {
      const avgTimeInCompany = averageTimeInCompanyOptions?.find(
        (it) => it?.value === average_time_in_company
      );
      aiScoringAttributes.average_time_in_company = avgTimeInCompany?.value
        ? avgTimeInCompany
        : {
            label: average_time_in_company,
            value: average_time_in_company,
          };
    } else {
      aiScoringAttributes.average_time_in_company = '';
    }
    if (auto_reject) {
      const autoRejectParsed = autoRejectParse?.[auto_reject];
      aiScoringAttributes.auto_reject = autoRejectParsed;
      if (autoRejectParsed) {
        aiScoringAttributes.auto_reject_threshold = auto_reject_threshold;
      }
      aiScoringAttributes.attribute_priority = aiScorePriority
        ?.filter((item) => item?.enabled)
        ?.map((item) => item?.id);
    }

    payload.ai_scoring_attributes = aiScoringAttributes;
  }

  if (isAiScoringV2) {
    payload.ai_scoring_attributes = {
      enabled: aiScoreToggle,
      auto_reject: auto_reject,
      auto_reject_threshold: auto_reject_threshold,
    };
  }

  const offices = processOffices(ats_office_id, initialOffices);
  if (offices?.length > 0) {
    payload.ats_job_offices_attributes = [...offices];
  }

  if (text) {
    payload.ats_job_description_attributes = { text };
  }
  return payload;
};

export const createDefaultValuesFromApi = (
  data = {},
  isAiScoringV2 = false
) => {
  const {
    basic_info = {},
    ai_scoring,
    description,
    compensation = {},
    additional_info = [],
    ...otherValues
  } = data;
  const { default_currency_country_id, base_salary = {} } = compensation;
  const { interval, max_amount, min_amount, currency_country_id } =
    base_salary || {};

  const {
    department,
    requisitions,
    office = [],
    ...otherBasic_info
  } = basic_info ?? {};

  const isCompensationEnabled = office.some(
    (office) => office?.compensation?.enabled
  );

  const defaultValues = {
    ...otherValues,
    ...otherBasic_info,
    ...ai_scoring,
    ...description,
    ...(isCompensationEnabled && {
      currency_country_id:
        currency_country_id ||
        office?.[0]?.compensation?.default_currency_country_id ||
        default_currency_country_id,
      min_amount,
      max_amount,
      interval,
    }),
    auto_reject: isAiScoringV2
      ? ai_scoring?.auto_reject ?? false
      : autoRejectMap?.[ai_scoring?.auto_reject] || 'no',
    aiScoreToggle: ai_scoring?.enabled ?? false,
    auto_reject_threshold: ai_scoring?.auto_reject_threshold || 20,
    experience: ai_scoring?.experience?.value || '',
    average_time_in_company: ai_scoring?.average_time_in_company?.value || '',
    aiScorePriority: ai_scoring?.attribute_priority?.length
      ? aiScorePriorityMap(ai_scoring?.attribute_priority)
      : [
          { id: 'skills', enabled: true },
          { id: 'education', enabled: true },
          { id: 'experience', enabled: true },
          { id: 'average_time_in_company', enabled: true },
        ],

    //adding custom fields default value
    ...additional_info?.reduce((acc, { field_id, value }) => {
      acc[`custom_${field_id}`] = value; // Use bracket notation
      return acc;
    }, {}),
  };

  if (department) {
    defaultValues.ats_department_id = department?.id;
  }

  // in ui, we are allowing only one for requisitions and office, so when BE
  // sends more than one, will populate the first value
  if (requisitions?.length > 0) {
    const hasRoleName = requisitions?.some((req) => Boolean(req?.role_name));

    if (hasRoleName) {
      // If requisitions have role_name, map all values
      defaultValues.requisitions_codes = requisitions?.map(
        (item) => item?.requisition_value
      );
    } else {
      // If requisitions don't have role_name, only send the first value
      defaultValues.requisitions_codes = requisitions[0]?.requisition_value;
    }
  }
  defaultValues.ats_office_id = office?.map((item) => item?.id);

  return defaultValues;
};

export const jdForm = (text, editorConfig, setEditorConfig, headerName) => {
  return [
    {
      type: 'custom',
      Field: EditorWithAi,
      props: {
        apiConfig: {
          getJobInfo: (formValues) => {
            const currentCompanyDomain =
              window.location.hostname?.split('.')?.[0];
            const defaultCompany =
              currentCompanyDomain.charAt(0).toUpperCase() +
              currentCompanyDomain.slice(1);

            const { id, experience, education, skills = [], name } = formValues;
            const finalName = headerName || name;
            const experienceValue = experience
              ? jobExperienceOptions.find((i) => i.value === experience)?.label
              : '';
            const educationValue = experience
              ? jobEducationOptions.find((i) => i.value === education)?.label
              : '';

            const metaPayload = {
              'Company Name': defaultCompany,
            };
            if (finalName) metaPayload.Title = finalName;
            if (educationValue)
              metaPayload['Education Qualification'] = educationValue;
            if (skills?.length > 0) metaPayload.Skills = skills;
            if (experienceValue) metaPayload.Experience = experienceValue;

            return {
              job_meta: JSON.stringify(metaPayload),
              job_id: id,
              job_template:
                '<h3>About Job</h3></p></p><h3>Skills & Qualification</h3><h3>Responsibilities</h3>',
            };
          },

          urls: {
            generate: 'job_descriptions/ats/generate',
            key: 'job_descriptions/ats/generation_status?key=',
            promptEdit: 'job_descriptions/ats/prompt_edit',
            aiActions: 'job_descriptions/ats/ai_edit',
          },
        },
        editorConfig,
        setEditorConfig,
        defaultValue: text,
      },
    },
  ];
};

export const jobInfoCollapseSectionProps = ({
  isFirst = false,
  isLast = false,
  feAiScoringV2,
}) => ({
  headerConfig: {
    containerStyles: (isOpen) => ({
      backgroundColor: 'primary-50',
      borderTopRadius: isFirst && '6px',
      borderBottomRadius: isLast && '6px',
      borderBottom: isOpen && '1px solid',
      borderColor: 'border-color',
      p: '15px',
    }),
    ...(!feAiScoringV2 && {
      textStyles: {
        color: 'primary-500',
      },
    }),
    iconStyles: {
      width: '24px',
      height: '24px',
      color: 'primary-500',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
    ...(feAiScoringV2 && {
      mb: '24px',
    }),
  },
  accordionItemProps: {
    overflow: 'visible',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em 2em',
    backgroundColor: 'white',
    borderRadius: '6px',
  },
});

export const jobInfoToastConfig = (type, message) => {
  const errorMap = {
    'Ats job salaries base err_salary_invalid_range':
      'Please fix the salary range',
    err_open_at_cannot_be_greater_than_target_date:
      'Target start date must be on or after the open date',
  };

  const config = {
    success: {
      title: `Job information saved`,
      status: 'success',
    },
    error: {
      title: `Error in saving job`,
      description: errorMap[message] || message,
      status: 'error',
    },
  };
  return config[type];
};
